import React from "react";
import { useState } from "react";

interface Faq {
  id: number;
  question: string;
  answer: string;
}

const faqs: Faq[] = [
  {
    id: 1,
    question: "How long does a typical repair take?",
    answer: "Most phone and tablet screen repairs can be completed in 1-2 hours. Battery replacements typically take 30-60 minutes. More complex repairs like water damage or data recovery may take 24-48 hours or longer depending on the extent of the damage. We'll provide an estimated completion time when you drop off your device."
  },
  {
    id: 2,
    question: "Do you offer a warranty on repairs?",
    answer: "Yes, all our repairs come with a parts and labor. If you experience any issues with the repaired component during this period, bring it back and we'll fix it free of charge. This warranty covers manufacturer defects and workmanship, but does not cover accidental damage or water damage after the repair."
  },
  {
    id: 3,
    question: "What brands do you repair?",
    answer: "We repair all major brands including iPhone, Samsung, Google Pixel, Huawei, LG, and more. We also repair laptops, tablets, and game consoles."
  },
  {
    id: 4,
    question: "Do you offer same-day service?",
    answer: "For most repairs, we can offer same-day service if you bring your device in before 2 PM. Complex repairs or repairs requiring special parts may take longer."
  },
  {
    id: 5,
    question: "Can you recover data from a broken device?",
    answer: "Yes, we offer professional data recovery services for devices with damaged screens, water damage, or other issues. Success rates vary depending on the extent of the damage."
  }
];

interface FaqItemProps {
  faq: Faq;
  isOpen: boolean;
  toggleFaq: () => void;
}

const FaqItem = ({ faq, isOpen, toggleFaq }: FaqItemProps) => {
  return (
    <div className="mb-6 border-b border-gray-200 pb-6">
      <button
        onClick={toggleFaq}
        className="flex justify-between items-center w-full text-left font-semibold text-lg mb-2 hover:text-primary focus:outline-none"
      >
        <span>{faq.question}</span>
        <svg
          className={`w-5 h-5 transform transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>
      <div className={`mt-2 ${isOpen ? "" : "hidden"}`}>
        <p className="text-gray-600">{faq.answer}</p>
      </div>
    </div>
  );
};

const FaqSection = () => {
  const [openFaqs, setOpenFaqs] = useState<Record<number, boolean>>({});

  const toggleFaq = (id: number) => {
    setOpenFaqs((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <section id="faq" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Find answers to common questions about our repair services.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {faqs.map((faq) => (
            <FaqItem
              key={faq.id}
              faq={faq}
              isOpen={openFaqs[faq.id] || false}
              toggleFaq={() => toggleFaq(faq.id)}
            />
          ))}
        </div>

        <div className="text-center mt-8">
          <p className="text-lg">Have more questions?</p>
          <a
            href="/contact"
            className="inline-block bg-primary text-white px-6 py-3 rounded-md font-medium hover:bg-opacity-90 transition-all mt-4"
          >
            Contact Our Support Team
          </a>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
